import { useAppSelector } from 'hooks/redux';
import type { Map as ImMap } from 'immutable';
import { useCallback, useMemo, useState } from 'react';

import { categoriesSelectors } from 'app-components/flux/categories';
import type { Category } from 'app-components/flux/categories/categoriesTypes';
import { freeToSpendSelectors } from 'app-components/flux/free-to-spend';
import type { PlannedSpendingConflict, PlannedSpendingItem } from 'app-components/flux/free-to-spend/freeToSpendTypes';

export interface PlannedCategoryCheckerProps {
  id?: string;
  startDate: string;
  isEdit?: boolean;
  isOneTime?: boolean;
}

export interface CoaType {
  type: string;
  id?: string;
  parentId?: string;
}

export default function usePlannedCategoryChecker(props: PlannedCategoryCheckerProps) {
  const categories = useAppSelector(categoriesSelectors.getCategoriesById);
  const initCategory = useAppSelector((state) => categoriesSelectors.getCategoryForId(state, props?.id || ''));

  // Gets PSI information for the current month by coa id (name, parentId, plannedId, filterId). Includes all coas in filter. (So if a parent is being tracked, it includes its children)
  const currentMonthPlannedCoas: ImMap<string, PlannedSpendingConflict> = useAppSelector((state) =>
    freeToSpendSelectors.getPlannedSpendingCoas(state, props.startDate, true),
  ); // Include uncategorized

  // Gets PSI information for future months by coa id (name, parentId, plannedId, filterId) Includes all coas in filter. (So if a parent is being tracked, it includes its children)
  const futurePlannedCoas: ImMap<string, PlannedSpendingConflict> = useAppSelector((state) =>
    freeToSpendSelectors.getFuturePlannedSpendingCoas(state, props.startDate, true),
  ); // Include uncategorized

  const [isAlreadyTracked, setIsAlreadyTracked] = useState(false);
  const [alreadyTrackedMessage, setAlreadyTrackedMessage] = useState('');
  const [errorText, setErrorText] = useState('');

  const newType = props?.id === '0' ? 'UNCATEGORIZED' : 'CATEGORY';
  const newId = initCategory?.id ?? (props?.id === '0' ? '0' : undefined);
  const newParentId = initCategory?.parentId ?? (props?.id === '0' ? '0' : undefined);

  const initialCoa = useMemo(
    () => ({ type: newType, id: newId, parentId: newParentId }),
    [newId, newParentId, newType],
  );

  const [coa, setCoa] = useState<Category | CoaType>(initialCoa);

  const children = useAppSelector((state) => categoriesSelectors.getChildCategoriesByParentId(state, coa?.id || ''))
    ?.toList()
    ?.toArray() as Category[];

  const currCategory = useAppSelector((state) => categoriesSelectors.getCategoryForId(state, coa?.id || ''));

  // Checks if child categories of newCatId exist in the current or future months
  // childCatName: The name of the PSI that tracks the discovered child category
  // isChildCategoryPresent: Does a child category of newCatId exist in the current month
  // isChildCategoryPresentInFuture: Does a child category of newCatId exist in a future month
  const childCategoryInfo = useCallback(
    (newCatId: string, itemFilterId?: string) => {
      const newChildren = categoriesSelectors
        .getChildCategoriesByParentIdUtil(categories, newCatId || '')
        ?.toList()
        ?.toArray() as Category[];
      let childCatName = '';
      let isChildCategoryPresent = false;
      let isChildCategoryPresentInFuture = false;

      // Checks if child categories are present in current or future months
      newChildren.forEach((childCat) => {
        const childCatExists = currentMonthPlannedCoas?.get(childCat?.id || ''); // Childcat exists in current month
        const isChildFromDifferentPSI = childCatExists?.filterId !== itemFilterId; // Just in case we are editing the PSI with the child cat
        if (childCatExists && childCat?.id !== initCategory?.id && isChildFromDifferentPSI) {
          isChildCategoryPresent = true;
          childCatName = childCatExists?.name;
        }

        const futureChildCatExists = futurePlannedCoas?.get(childCat?.id || ''); // Childcat exists in future month
        const isFutureChildFromDifferentPSISeries = futureChildCatExists?.filterId !== itemFilterId; // Just in case we are editing the series with the child cat
        if (
          !props.isOneTime &&
          futureChildCatExists &&
          childCat?.id !== initCategory?.id &&
          isFutureChildFromDifferentPSISeries
        ) {
          isChildCategoryPresentInFuture = true;
        }
      });
      return [childCatName, isChildCategoryPresent, isChildCategoryPresentInFuture];
    },
    [categories, currentMonthPlannedCoas, initCategory?.id, futurePlannedCoas, props.isOneTime],
  );

  const validate = useCallback(
    (c: CoaType | Category, plannedItem: PlannedSpendingItem | null = null) => {
      const itemFilterId = plannedItem?.filterId; // Check if a conflicting planned item is the same as the series we are editing.

      let isErrorCurrentMonth = false; // Prioritize showing errors for current month values rather than future months!

      // returns the information of the PSI (name, parentId, plannedId, filterId) the coa already exists in for the current month!
      const currentMonthExistingPSI = currentMonthPlannedCoas?.get(c?.id || '');

      const [childCatName, isChildCategoryPresent, isChildCategoryPresentInFuture] = childCategoryInfo(
        c?.id || '',
        plannedItem?.filterId,
      );

      // ==== Check for conflicts in CURRENT MONTH ==== //
      if (
        currentMonthExistingPSI && // PSI that filters the same categories already exists (Could be same PSI we are editing)
        currentMonthExistingPSI?.filterId !== itemFilterId // Ensure that the existing filterId does NOT equal this items filterId! (If we are editing the conflicting PSI, there should be no problems!)
      ) {
        isErrorCurrentMonth = true;
        setErrorText(
          `Already tracking with ${currentMonthExistingPSI?.name}. Each category and its subcategories can only be counted once.`,
        );
      } else if (isChildCategoryPresent) {
        isErrorCurrentMonth = true;
        setErrorText(
          `Already tracking with ${childCatName}. Each category and its subcategories can only be counted once.`,
        );
      } else {
        setErrorText('');
      }

      // ==== Check for conflicts in FUTURE MONTHS ==== //
      if (!props.isOneTime) {
        // This lets us know if the category itself is tracked in future months! (Includes all coas in filter)
        const futureMonthExistingPSI = futurePlannedCoas?.get(c?.id || '');
        const doesItemConflictInFuture = futureMonthExistingPSI && futureMonthExistingPSI.filterId !== itemFilterId;

        // Show unique already tracked error if PSI doesn't exist in current month (Because it creates a one time expense)
        // AND if the coa exists in the future or a child cat exists in the future or a parent cat exists in the future
        setIsAlreadyTracked(
          Boolean(!currentMonthExistingPSI && (!!doesItemConflictInFuture || isChildCategoryPresentInFuture)),
        );

        // ==== Check for conflicts in FUTURE MONTHS ==== //
        if (doesItemConflictInFuture || isChildCategoryPresentInFuture) {
          if (props.isEdit && !isErrorCurrentMonth) {
            setErrorText('This category is already tracked in future months.');
          }
          if (!currentMonthExistingPSI) {
            // We only create a one-time expense if it doesn't already exist
            setAlreadyTrackedMessage(
              'This category is already tracked in future months. By continuing, a one-time expense will be created.',
            );
          }
        }
      }
      return c;
    },
    [childCategoryInfo, currentMonthPlannedCoas, futurePlannedCoas, props.isEdit, props.isOneTime],
  );

  // On coa change based on user input
  const coaSetter = useCallback(
    (c: CoaType | Category | null, plannedItem: PlannedSpendingItem | null = null) => {
      if (c === null) {
        setCoa({
          id: undefined,
          parentId: undefined,
          type: '',
        });
        return;
      }
      setCoa(c); // Set coa here, no more changes to c are made.
      validate(c, plannedItem);
    },
    [validate],
  );

  // Why are we returning a tuple and not an object?
  return useMemo<
    [
      (c: CoaType | Category, plannedItem?: PlannedSpendingItem | null) => CoaType | Category,
      Category | CoaType,
      (c: CoaType | Category | null, plannedItem?: PlannedSpendingItem | null) => void,
      string,
      Category | undefined,
      Category | undefined,
      boolean,
      Category[],
      string,
    ]
  >(
    () => [
      validate,
      coa,
      coaSetter,
      errorText,
      currCategory,
      initCategory,
      isAlreadyTracked,
      children,
      alreadyTrackedMessage,
    ],
    [
      validate,
      alreadyTrackedMessage,
      initCategory,
      children,
      coa,
      coaSetter,
      currCategory,
      errorText,
      isAlreadyTracked,
    ],
  );
}
